<template>
    <Head title="Tracker vos colis" />

    <FrontLayout>
        <div class="w-full text-xl text-gray-800 leading-normal mb-6">
            <h1 class="font-bold font-sans break-normal text-gray-900 pt-6 text-3xl md:text-4xl">
                👋 Bienvenue sur notre système de tracking
            </h1>
            <p class="text-sm font-normal text-gray-600 ml-8">
                Utilisez notre outil de recherche pratique pour connaitre l'évolution de votre colis.
            </p>
        </div>
        <div class="grid grid-cols-5 gap-4">
            <div class="col-span-full sm:col-span-2 px-2">
                <img style="height: 325px !important" src="/assets/img/package.svg">
            </div>
            <div class="col-span-full sm:col-span-3 font-bold bg-slate-200 rounded-lg tracking-shadow px-5 py-10 text-center border-b-2 border-slate-400 rounded-sm">
                <div>
                    <h2 class="text-xl md:text-3xl uppercase text-slate-600">Tracker votre colis</h2>
                    <h3 class="text-gray-600 text-sm md:text-base text-center text-slate-500">
                        <span>Saisissez le code de votre colis:</span>
                        <i @click="showHelpModal()" class="fad fa-lightbulb cursor-pointer text-yellow-600 text-lg ml-1"></i>
                    </h3>
                    <div class="w-full text-center py-8">
                        <div>
                            <div v-if="status === 'form'">
                                <form @submit.prevent="track">
                                    <div class="max-w-xl mx-auto p-1 pr-0 flex flex-wrap items-center space-x-2">
                                        <input type="text" placeholder="X-XXXX" v-model="code" required autocomplete="off"
                                            class="flex-1 text-2xl mt-4 appearance-none border border-gray-400 rounded-lg shadow-md py-4 text-gray-500 focus:outline-none text-center"
                                        >
                                        <BotiButton :loader="loading" :disabled="loading || !code" @click.prevent="track" color="sky"
                                            :text="loading ? 'Tracking' : 'Tracker'"
                                            class="flex-1 block md:inline-block tracking-wider uppercase py-6 rounded-lg mt-4 text-2xl"
                                        >
                                            <template #icon v-if="loading">
                                                <i class="fad fa-spinner"></i>
                                            </template>
                                            <template #icon v-else>
                                                <i class="fad fa-search"></i>
                                            </template>
                                        </BotiButton>
                                    </div>
                                </form>
                            </div>

                            <!-- RESULTAT -->
                            <div v-if="status === 'result'">
                                <div v-if="trackedPackage">
                                    <div :class="'border-l-4 bg-' + trackedPackage.status_color + '-100 border-' + trackedPackage.status_color + '-700 rounded-b text-' + trackedPackage.status_color + '-700 p-2 shadow-md mb-6 font-bold text-xl'">
                                        Statut du colis: {{ trackedPackage.status_text }}
                                    </div>
                                    <div class="w-full mt-4">
                                        <div class="relative">
                                            <div class="absolute left-0 top-1/2 w-full h-0.5 bg-gray-200"></div>

                                            <ul class="relative flex justify-between w-full">
                                                <li v-for="(history, index) in trackedPackage.historical" :key="index">
                                                    <div class="w-5 h-5 rounded-full border-2"
                                                        :class="history.pass ? 'bg-' + history.status_color + '-500 border-' + history.status_color + '-700' : 'bg-white border-gray-400'"
                                                    ></div>
                                                </li>
                                            </ul>
                                        </div>

                                        <div class="flex justify-between text-xs mt-2 text-gray-600 uppercase">
                                            <div v-for="(history, index) in trackedPackage.historical" :key="index" class="text-center">
                                                <div :class="history.pass ? 'text-' + history.status_color + '-700' : 'text-gray-500'">{{ history.name }}</div>
                                                <div class="text-gray-400">{{ history.pass ? history.date : 'Bientôt' }}</div>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <!-- ERREUR -->
                                <div v-else class="font-semibold border-l-4 bg-yellow-100 border-yellow-700 rounded-b text-yellow-700 px-2 py-5 shadow-md mb-6">
                                    Le code <b>{{ code }}</b> ne correspond à aucun colis enregistré chez nous.<br>
                                    Veuillez vérifier que vous avez saisir le bon code. Merci 😊
                                </div>

                                <!-- RESET -->
                                <div class="text-center mt-4">
                                    <BotiButton @click="resetTracking" text="Effectuer une nouvelle recherche" color="sky"
                                        class="tracking-wider uppercase p-4 rounded-lg text-sm"
                                    >
                                        <template #icon><i class="fad fa-search ml-1"></i></template>
                                    </BotiButton>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <div class="mt-8 mb-44" id="list-travel">
            <div class="w-full text-xl text-gray-800 leading-normal mb-6">
                <h1 class="font-bold font-sans break-normal text-gray-900 pt-6 text-3xl md:text-4xl">
                    <i class="fad fa-ship text-yellow-500"></i> Nos prochaines expéditions
                </h1>
                <p class="text-sm font-normal text-gray-600 ml-10">Ci-dessous, la liste de nos prochains départs</p>
            </div>
            <div class="grid grid-cols-2 gap-4">
                <div class="col-span-full sm:col-span-1 bg-white tracking-shadow rounded-b-xl border-r-2 border-l-2 border-b-2 border-sky-200"
                    v-for="(travel, index) in travels.data" :key="index"
                >
                    <p class="flex justify-between items-center text-base text-sky font-bold md:text-xl leading-none px-4 py-1 bg-sky-200">
                        <span>Départ: {{ travel.departure_country.name }}</span>
                        <i class="fad fa-angle-double-right text-sky"></i>
                        <span>Arrivée: {{ travel.arrival_country.name }}</span>
                    </p>
                    <hr>
                    <div class="flex w-full items-center font-sans p-4">
                        <div class="flex-1 px-2">
                            <p class="flex flex-col space-y-1 text-gray-600 text-small md:text-sm">
                                <span><i class="fa fa-clock"></i> Départ prévu le: <b class="capitalize">{{ formattedDate(travel.departure_date) }}</b> </span>
                                <span class="text-gray-600">
                                    <i class="fa fa-clock"></i> Arrivée prévue le: <b class="capitalize">{{ formattedDate(travel.arrival_date) }}</b>
                                </span>
                            </p>
                            <hr class="my-2 mr-8">
                            <p class="flex flex-col space-y-1 text-gray-600 text-small md:text-sm">
                                <span><i class="fa fa-clock"></i> Début de collecte: <b class="capitalize">{{ formattedDate(travel.collection_start_at) }}</b> </span>
                                <span class="text-gray-600">
                                    <i class="fa fa-clock"></i> Fin de collecte: <b class="capitalize">{{ formattedDate(travel.collection_end_at) }}</b>
                                </span>
                            </p>
                        </div>
                        <div class="justify-end">
                            <button @click.prevent="showModal(travel)" class="btn bg-sky-200 border border-sky-500 hover:bg-white
                                text-xs text-sky hover:text-sky-400 hover:border-sky-400 font-bold py-2 px-4 rounded-full"
                            >
                            <span class="hidden lg:block">Envoyer votre demande</span>
                                <i class="fad fa-envelope lg:ml-2 text-sm"></i>
                            </button>
                        </div>
                    </div>
                </div>
                <div v-if="! travels.total" class="col-span-2 bg-front-color-primary
                    py-4 px-2 rounded-lg border-l-4 border-sky-500"
                >
                    <div class="flex items-center space-x-2 text-sky-500">
                        <div>
                            <i class="fad fa-info-circle text-lg mr-1"></i>
                            <span class="text-lg underline">Information:</span>
                        </div>
                        <div class="text-sky-200">
                            Aucune expédition progammée pour l'instant! Veuillez revenir plus tard.
                        </div>
                        <div class="text-sky-200">
                            À très bientôt 😊.
                        </div>
                    </div>
                </div>
            </div>
            <div class="my-4">
                <Pagination :links="travels.links" />
            </div>
        </div>

        <JetDialogModal :show="manageHelpModal" @close="closeHelpModal" max-width="xl">
            <template #title>
                <span class="text-2xl">Où trouver mon numéro de colis ?</span>
            </template>
            <template #content>
                <div class="flex flex-col space-y-4">
                    <p>
                        Votre numéro de colis vous a été transmis par sms après sa création.
                        Vous pouvez aussi le retrouver dans la
                        <a class="font-bold text-blue-600" title="Mes colis" :href="route('front.packages.show')">
                            liste de vos colis
                        </a> une fois connecté.
                    </p>
                    <div>Besoin d'aide ?<br> N'hésitez pas à entrer en contact avec nous:</div>
                    <ul>
                        <li>Sur WhatsApp:
                            <span class="font-bold text-gray-700">
                                {{ $page.props.contactSettings.phone ? '+' + $page.props.contactSettings.dial_code + ' '+ $page.props.contactSettings.phone : '' }}
                            </span>
                        </li>
                        <li>
                            Ou par mail:
                            <span class="font-bold text-gray-700">{{ $page.props.contactSettings.email }}</span>.
                        </li>
                    </ul>
                </div>
            </template>
            <template #footer>
                <BotiButton @click="closeHelpModal" text="Fermer" color="red">
                    <template #icon><i class="fad fa-times-circle text-sm ml-1"></i></template>
                </BotiButton>
            </template>
        </JetDialogModal>

        <JetDialogModal :show="manageModal" @close="closeModal" max-width="2xl">
            <template #title>
                Demande d'envoi de colis:
                <span class="text-sky">{{ travel.departure_country.name }}</span> vers
                <span class="text-sky">{{ travel.arrival_country.name }}</span>
            </template>

            <template #content>
                <div class="grid grid-cols-5 gap-4">
                    <div class="col-span-2 sm:col-span-2">
                        <div class="flex flex-col text-left items-center">
                            <div>
                                <div class="flex items-center justify-center mb-2 rounded-full">
                                    <img src="/images/request.png" />
                                </div>
                                <p class="mb-2 font-bold text-center uppercase text-sky">
                                    À lire attentivement
                                </p>
                                <p class="text-sm text-gray-900 font-semibold">
                                    Pour une meilleure prise en charge de votre demande,
                                    veuillez renseigner le formulaire ci-après avec des informations correctes.
                                    <br>Nous vous recontacterons dans de plus brefs délais.
                                </p>
                                <hr class="my-3">
                                <p class="mb-2 font-bold text-center uppercase text-orange-500">
                                    Avez vous un compte?
                                </p>
                                <p class="text-sm text-gray-900 font-semibold">
                                    Si vous avez déjà un compte chez <b>Boti Cargo Shipping</b>,
                                    veuillez vous connecter avant d'envoyer votre demande.
                                </p>
                            </div>
                            <div class="w-full h-1 ml-auto duration-300 origin-left transform scale-x-0 bg-deep-purple-accent-400 group-hover:scale-x-100"></div>
                        </div>
                    </div>
                    <div class="col-span-3 sm:col-span-3 border-l border-gray-400 pl-4">
                        <div class="mb-2">
                            <JetLabelRequired for="requester_name" value="Nom & prénom(s)" />
                            <JetInput v-if="!$page.props.user" id="requester_name" type="text" autocomplete="off"
                                v-model="form.requester_name" placeholder="Entrez votre nom & prénom(s)"
                            />
                            <div v-else class="form-input w-full">
                                {{ $page.props.user.name }}
                            </div>
                            <JetInputError v-for="(error, index) in form.errors.requester_name" :key="index" :message="error" />
                        </div>
                        <div v-if="!$page.props.user" class="mb-2">
                            <JetLabel for="requester_email" value="Adresse mail:" />
                            <JetInput id="requester_email" type="email" autocomplete="off"
                                v-model="form.requester_email" placeholder="Entrez votre adresse email"
                            />
                            <JetInputError v-for="(error, index) in form.errors.requester_email" :key="index" :message="error" />
                        </div>
                        <div v-if="!$page.props.user" class="mb-2">
                            <JetLabelRequired for="sender_phone" value="Numéro de téléphone" />
                            <vue-tel-input required @input="phoneInput"
                                :defaultCountry="form.phone"
                                :value="form.phone"
                            />
                            <JetInputError v-for="(error, index) in form.errors.phone" :key="index" :message="error" />
                        </div>
                        <div class="mb-2">
                            <JetLabel for="weight_package" value="Nombre de colis:" />
                            <JetInput id="weight_package" type="number" min="0" placeholder="O"
                                class="text-right" v-model="form.weight_package" />
                            <JetInputError v-for="(error, index) in form.errors.weight_package" :key="index" :message="error" />
                        </div>
                        <div class="mb-2">
                            <JetLabelRequired for="description" value="Description du colis:" />
                            <textarea class="form-input w-full" v-model="form.description"
                                id="description" cols="30" rows="5" maxlength="160"
                                placeholder="Entrer une brève description de votre colis..."
                            ></textarea>
                            <JetInputError v-for="(error, index) in form.errors.description" :key="index" :message="error" />
                        </div>
                        <div>
                            <JetLabelRequired for="address" value="Adresse de récupération du colis" />
                            <textarea class="form-input w-full" v-model="form.address"
                                id="address" cols="30" rows="5" maxlength="160"
                                placeholder="Entrer le lieu de récupération de colis..."
                            ></textarea>
                            <JetInputError v-for="(error, index) in form.errors.address" :key="index" :message="error" />
                        </div>
                    </div>
                </div>
            </template>

            <template #footer>
                <BotiButton @click="closeModal" text="Quitter" color="red">
                    <template #icon><i class="fad fa-times-circle text-sm ml-1"></i></template>
                </BotiButton>

                <BotiButton :loader="loading" :disabled="loading"
                    @click="sendRequest" color="greendack" class="ml-2"
                    :class="{ 'opacity-25' : loading }" text="Envoyer"
                >
                    <svg v-if="loading" class="animate-spin w-4 h-4 fill-current shrink-0" viewBox="0 0 16 16">
                        <path d="M8 16a7.928 7.928 0 01-3.428-.77l.857-1.807A6.006 6.006 0 0014 8c0-3.309-2.691-6-6-6a6.006 6.006 0 00-5.422 8.572l-1.806.859A7.929 7.929 0 010 8c0-4.411 3.589-8 8-8s8 3.589 8 8-3.589 8-8 8z"></path>
                    </svg>
                    <template #icon>
                        <i class="fad fa-envelope text-sm"></i>
                    </template>
                </BotiButton>
            </template>
        </JetDialogModal>
    </FrontLayout>
</template>

<script>
    import { defineComponent } from 'vue'
    import JetLabel from '@/Jetstream/Label'
    import JetInput from '@/Jetstream/Input'
    import { Inertia } from '@inertiajs/inertia'
    import BotiButton from '@/Components/Button'
    import Pagination from '@/Components/Pagination'
    import JetInputError from '@/Jetstream/InputError'
    import FrontLayout from '@/Layouts/FrontLayout.vue'
    import JetDialogModal from '@/Jetstream/DialogModal'
    import { Head, Link } from '@inertiajs/inertia-vue3';
    import DateFormatMixins from '@/Mixins/DateFormatMixins'
    import JetLabelRequired from '@/Jetstream/LabelRequired.vue'
    import RebuildPhoneMixins from '@/Mixins/RebuildPhoneMixins'

    export default defineComponent({
        components: {
            Head,
            Link,
            JetLabel,
            JetInput,
            BotiButton,
            Pagination,
            FrontLayout,
            JetInputError,
            JetDialogModal,
            JetLabelRequired
        },

        mixins: [RebuildPhoneMixins, DateFormatMixins],

        props: {
            travels: Object,
            contactSettings: Object,
            package: Object,
            code: String
        },

        data() {
            return {
                showForm: true,
                code: null,
                manageModal: false,
                manageHelpModal: false,

                form: this.$inertia.form({
                    requester_name: null,
                    requester_email: null,
                    phone: null,
                    dial_code: null,
                    country_code: null,
                    weight_package: null,
                    description: null,
                    address: null
                }),

                loading: false,
                tracked: false,
                trackedPackage: null,
                status: 'form',

                travel: null
            }
        },

        mounted() {
            const urlCode = new URLSearchParams(window.location.search).get('code')

            if (urlCode) {
                this.code = urlCode
                this.track()
            }
        },

        methods: {
            closeHelpModal() {
                this.manageHelpModal = false
            },

            showHelpModal() {
                this.manageHelpModal = true
            },

            closeModal() {
                this.manageModal = false
                this.form.clearErrors()
                this.form.reset()
            },

            showModal(travel) {
                this.travel = travel
                this.form.clearErrors()
                this.manageModal = true
            },

            track() {
                if (!this.code || this.loading) return

                this.loading = true
                this.trackedPackage = null
                this.status = 'form'

                axios.post(route('front.package.track'), {
                    code: this.code
                })
                .then((response) => {
                    this.trackedPackage = response.data
                })
                .catch((error) => {
                    if (error.response?.status === 404) {
                        this.trackedPackage = null
                    }

                    if (error.response?.status === 422) {
                        console.log(error.response.data.errors)
                    }

                })
                .finally(() => {
                    this.loading = false
                    this.status = 'result'
                })
            },

            resetTracking() {
                this.code = null
                this.trackedPackage = null
                this.status = 'form'
                this.loading = false

                // ✅ enlève ?code de l'URL sans reload
                Inertia.visit(route('front.travels.index'), {
                    replace: true,
                    preserveState: true,
                    preserveScroll: true
                })
            },

            sendRequest() {
                this.loading = true
                var that = this
                 axios.post(route('front.travel.requests.send', this.travel),
                    {
                        requester_name: this.form.requester_name,
                        requester_email: this.form.requester_email,
                        phone: this.form.phone,
                        dial_code: this.form.dial_code,
                        country_code: this.form.country_code ,
                        weight_package: this.form.weight_package,
                        description: this.form.description,
                        address: this.form.address
                    }
                  ).then(function(response) {
                        that.$swal.fire(
                            'Demande',
                            'Votre demande d\'envoi de colis à été envoyée avec succès.\nNous vous contacterons dans de brefs délais.',
                            'success'
                        )
                        that.loading = false
                        that.closeModal();

                    }).catch((error) => {
                        this.form.errors = error.response.data.errors
                        that.loading = false
                    })
            }

        }
    })
</script>

<style scoped>
    .tracking-shadow { box-shadow: -3.119px 14.672px 18px 0px rgba(90, 90, 90, 0.15) !important }
</style>
